<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import { api } from '@/api/client';
import { useCreateStore } from '@/stores/create';
import { useUserStore } from '@/stores/user';
import { haptic } from '@/composables/useTelegramWebApp';
import MxButton from '@/components/MxButton.vue';
import MxCard from '@/components/MxCard.vue';
import MxIcon from '@/components/MxIcon.vue';
import MxIconShelf from '@/components/MxIconShelf.vue';
import MxSegmented from '@/components/MxSegmented.vue';
import MxTextarea from '@/components/MxTextarea.vue';
import MxPromptChips from '@/components/MxPromptChips.vue';
import { findTemplate } from '@/lib/templates';
import { MODES } from '@/lib/modes';
import { promptSuggestionsFor, appendSuggestion, type PromptSuggestion } from '@/lib/prompt-suggestions';
import type { ModelInfo, UploadOut } from '@/api/types';

const MODEL_EMOJI: Record<string, string> = {
  seedance_2: '🚀',
  seedance_2_fast: '⚡',
  grok_imagine: '✨',
  wan_2_6_flash: '🌊',
  veo3_fast: '🎬',
  veo3: '🎥',
  kling_3_motion: '🎭',
  gpt_image_2: '🖼',
  nano_banana_2: '🍌',
};

const modeShelf = MODES.map((m) => ({ id: m.kind, icon: m.icon, label: m.label }));

const route = useRoute();
const router = useRouter();
const create = useCreateStore();
const userStore = useUserStore();
const { params, models, cost, canAfford, submitError } = storeToRefs(create);

const modelShelf = computed(() =>
  models.value.map((m) => ({
    id: m.key,
    emoji: MODEL_EMOJI[m.key] ?? '🖼',
    label: m.label,
  })),
);

onMounted(async () => {
  create.reset('image');
  await create.loadModels('image');

  const tplId = route.query.template;
  const modelKey = route.query.model;
  if (typeof tplId === 'string') {
    const t = findTemplate(tplId);
    if (t && t.kind === 'image') create.applyTemplate(t);
  } else if (typeof modelKey === 'string') {
    const m = create.models.find((x) => x.key === modelKey);
    if (m) await pickModel(m);
  }
  await create.estimate();
  try { localStorage.setItem('last_create_kind', 'image'); } catch { /* */ }
});

const validation = computed(() => create.validate());
const payDisabled = computed(() => validation.value !== null || cost.value === null || !canAfford.value);
const payLabel = computed(() => {
  if (validation.value) return validation.value;
  if (cost.value === null) return 'Считаю...';
  if (!canAfford.value) return `Не хватает ${cost.value} т.`;
  return `Сгенерировать · ~${cost.value} т.`;
});

async function pickModel(m: ModelInfo) {
  params.value.model_key = m.key;
  await create.estimate();
}

async function pickRef() {
  const inp = document.createElement('input');
  inp.type = 'file';
  inp.accept = 'image/*';
  inp.onchange = async () => {
    const f = inp.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append('file', f);
    try {
      const out = await api.postMultipart<UploadOut>('/uploads', fd);
      params.value.reference_image_urls = [out.url];
    } catch {
      // upload failure is silent here — surfaced via submitError on pay
    }
  };
  inp.click();
}

async function pay() {
  haptic.impact('medium');
  const out = await create.submit();
  if (out) {
    await userStore.load();
    await router.push(`/generation/${out.id}`);
  } else {
    haptic.notification('error');
  }
}

function onSegmentChange(t: 'video' | 'image') {
  if (t === 'video') void router.push('/create/video');
}

const promptSuggestions = promptSuggestionsFor('image');
function applySuggestion(s: PromptSuggestion) {
  params.value.prompt = appendSuggestion(params.value.prompt, s.text);
  void create.estimate();
}
</script>

<template>
  <section class="ci">
    <MxIconShelf
      :items="modeShelf"
      active-id="image"
      size="lg"
      @update:active-id="(id) => onSegmentChange(id as 'video' | 'image')"
    />

    <MxIconShelf
      :items="modelShelf"
      :active-id="params.model_key"
      @update:active-id="(id) => { const m = models.find(x => x.key === id); if (m) void pickModel(m); }"
    />

    <MxTextarea v-model="params.prompt" placeholder="Опишите картинку" :min-rows="4" />
    <MxPromptChips :items="promptSuggestions" @pick="applySuggestion" />

    <MxCard class="ref" clickable @click="pickRef">
      <MxIcon name="Image" />
      <span>{{ (params.reference_image_urls?.length ?? 0) > 0 ? 'Референс загружен — заменить' : 'Опциональный референс' }}</span>
    </MxCard>

    <div class="param-block">
      <p class="row__lbl">Размер</p>
      <MxSegmented
        :model-value="params.size ?? '1024x1024'"
        :options="[
          { value: '1024x1024', label: '1:1' },
          { value: '1024x1536', label: '2:3' },
          { value: '1536x1024', label: '3:2' },
        ]"
        @update:model-value="(v) => { params.size = v as any; void create.estimate(); }"
      />
    </div>

    <div class="param-block">
      <p class="row__lbl">Количество</p>
      <MxSegmented
        :model-value="String(params.count ?? 1)"
        :options="[
          { value: '1', label: '1' },
          { value: '2', label: '2' },
          { value: '4', label: '4' },
        ]"
        @update:model-value="(v) => { params.count = Number(v); void create.estimate(); }"
      />
    </div>

    <p v-if="submitError" class="alert">{{ submitError }}</p>

    <div class="paybar">
      <MxButton :disabled="payDisabled" @click="pay" variant="primary">{{ payLabel }}</MxButton>
    </div>
  </section>
</template>

<style scoped>
.ci { padding: var(--sp-3) 0 120px; display: flex; flex-direction: column; gap: var(--sp-3); }
.row { display: flex; align-items: center; justify-content: space-between; }
.row__main { display: flex; flex-direction: column; gap: 4px; }
.row__lbl { font-size: var(--fs-sm); color: var(--text-secondary); }
.row__val { font-weight: 600; }
.param-block { display: flex; flex-direction: column; gap: var(--sp-2); }
.ref { display: flex; align-items: center; gap: var(--sp-2); }
.alert { color: var(--error); font-size: var(--fs-sm); padding: var(--sp-2); background: var(--bg-elevated); border-radius: var(--r-md); }
.alert--warn { color: var(--warning); }
.paybar {
  position: fixed; bottom: 64px; left: 50%; transform: translateX(-50%);
  width: 100%; max-width: 480px; padding: var(--sp-3) var(--sp-4) calc(var(--sp-3) + env(safe-area-inset-bottom));
  background: linear-gradient(transparent, var(--bg-base) 30%);
  z-index: 30;
}
.paybar .btn { width: 100%; }
.models { display: flex; flex-direction: column; gap: var(--sp-2); }
.model { display: flex; flex-direction: column; gap: 4px; padding: var(--sp-3); border-radius: var(--r-md); background: var(--bg-overlay); width: 100%; text-align: left; }
.model--active { border: 1px solid var(--accent); }
.model__lbl { font-weight: 600; }
.model__hint { color: var(--text-secondary); font-size: var(--fs-sm); }
</style>
